import { useEffect, useMemo, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { InputSwitch } from 'primereact/inputswitch'
import { Plus, X, Pencil, Trash2, ShieldCheck } from 'lucide-react'
import { useSipTrunkAcl } from '../../../hooks/useSipTrunkAcl.js'
import { useToast } from '../../system/ToastProvider.js'
import { SectionCard } from '../DrawerFormKit.js'

export interface SipTrunkAclTabProps {
  trunkId: string | null
}

interface AclForm {
  status: boolean
  aclName: string
  aclDescription: string
  aclIps: string[]
}

const emptyForm: AclForm = {
  status: true,
  aclName: '',
  aclDescription: '',
  aclIps: [''],
}

export function SipTrunkAclTab({ trunkId }: SipTrunkAclTabProps): JSX.Element {
  const toast = useToast()
  const { items, loading, refetch, create, update, remove, getById } = useSipTrunkAcl()
  const [form, setForm] = useState<AclForm>(emptyForm)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (trunkId) {
      void refetch()
    }
  }, [trunkId, refetch])

  const filtered = useMemo(
    () => (trunkId ? items.filter((acl) => acl.sipTrunkId === trunkId) : []),
    [items, trunkId],
  )

  const reset = () => {
    setForm(emptyForm)
    setEditingId(null)
  }

  const handleSave = async () => {
    if (!trunkId) return

    const name = form.aclName.trim()
    if (!name) {
      toast.error('ACL Name is required')
      return
    }
    const ips = form.aclIps.map((ip) => ip.trim()).filter((ip) => ip.length > 0)
    if (ips.length === 0) {
      toast.error('At least one IP CIDR is required')
      return
    }

    const payload = {
      status: form.status,
      sipTrunkId: trunkId,
      aclName: name,
      aclDescription: form.aclDescription.trim() || null,
      ips: ips.map((ip) => ({ ipCidr: ip })),
    }

    setSaving(true)
    try {
      if (editingId) {
        await update(editingId, payload)
        toast.success('ACL updated')
      } else {
        await create(payload)
        toast.success('ACL created')
      }
      await refetch()
      reset()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save ACL')
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async (id: string) => {
    try {
      const { item, ips } = await getById(id)
      setEditingId(id)
      setForm({
        status: item.status ?? true,
        aclName: item.aclName ?? '',
        aclDescription: item.aclDescription ?? '',
        aclIps: ips.length > 0 ? ips.map((ip) => ip.ipCidr) : [''],
      })
    } catch {
      toast.error('Failed to load ACL')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await remove(id)
      toast.success('ACL deleted')
      if (editingId === id) reset()
      await refetch()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete ACL')
    }
  }

  const setIp = (index: number, value: string) => {
    const next = [...form.aclIps]
    next[index] = value
    setForm({ ...form, aclIps: next })
  }

  const addIp = () => setForm({ ...form, aclIps: [...form.aclIps, ''] })

  const removeIp = (index: number) => {
    const next = form.aclIps.filter((_, i) => i !== index)
    setForm({ ...form, aclIps: next.length > 0 ? next : [''] })
  }

  if (!trunkId) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-6 text-sm text-gray-500">
        Create and save this trunk before managing its ACLs.
      </div>
    )
  }

  return (
    <div className="space-y-5 mb-6">
      {/* --- ACL form card --- */}
      <SectionCard
        title={editingId ? 'Edit ACL' : 'New ACL'}
        description="Define an access control list with allowed IP/CIDR ranges for this trunk."
        icon="pi pi-shield"
        bodyClassName="px-5 py-5 space-y-4"
      >
        {/* Status toggle */}
        <div className="flex items-center gap-3">
          <InputSwitch
            checked={form.status}
            onChange={(e) => setForm({ ...form, status: e.value })}
            disabled={saving}
          />
          <span className="text-sm font-medium text-gray-700">
            {form.status ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* ACL Name */}
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
            ACL Name
          </label>
          <InputText
            value={form.aclName}
            onChange={(e) => setForm({ ...form, aclName: e.target.value })}
            placeholder="e.g. Office-Network"
            className="w-full"
            disabled={saving}
          />
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
            Description
          </label>
          <InputTextarea
            value={form.aclDescription}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setForm({ ...form, aclDescription: e.target.value })
            }
            placeholder="Optional notes about this ACL"
            rows={2}
            className="w-full"
            disabled={saving}
          />
        </div>

        {/* IP CIDR list */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
            Allowed IP / CIDR Ranges
          </label>
          {form.aclIps.map((ip, index) => (
            <div key={index} className="flex items-center gap-2">
              <InputText
                value={ip}
                onChange={(e) => setIp(index, e.target.value)}
                placeholder="10.0.0.0/24"
                className="flex-1"
                disabled={saving}
              />
              <button
                type="button"
                onClick={() => removeIp(index)}
                disabled={saving}
                aria-label="Remove IP"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-400 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addIp}
            disabled={saving}
            className="flex items-center gap-1.5 text-sm font-medium text-[#3454cf] transition-colors hover:text-[#2640a8] disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Add IP Range
          </button>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 border-t border-gray-100 pt-4">
          <Button
            type="button"
            label={editingId ? 'Update ACL' : 'Create ACL'}
            icon="pi pi-check"
            loading={saving}
            disabled={saving}
            className="p-button-sm"
            onClick={handleSave}
          />
          {editingId && (
            <Button
              type="button"
              label="Cancel"
              className="p-button-text p-button-sm"
              onClick={reset}
              disabled={saving}
            />
          )}
        </div>
      </SectionCard>

      {/* --- Existing ACLs list --- */}
      <SectionCard
        title="Linked ACLs"
        description="ACLs currently associated with this trunk."
        icon="pi pi-list"
        bodyClassName="px-5 py-4"
      >
        {loading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-gray-500">
            <i className="pi pi-spin pi-spinner" />
            Loading ACLs…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ShieldCheck className="h-8 w-8 text-gray-300" />
            <p className="mt-2 text-sm text-gray-500">No ACLs linked to this trunk.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filtered.map((acl) => (
              <div
                key={acl.id}
                className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-900">{acl.aclName}</span>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        acl.status
                          ? 'bg-green-50 text-green-600'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {acl.status ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {acl.aclDescription && (
                    <p className="mt-0.5 truncate text-xs text-gray-500">{acl.aclDescription}</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleEdit(acl.id)}
                    aria-label="Edit ACL"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-500"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(acl.id)}
                    aria-label="Delete ACL"
                    className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  )
}
